import { useState } from "react";
import {
  type EditableCustomDashboardConfig,
  isValidDashboardUrl,
} from "../../luzmo/custom-dashboard-config";

interface CustomDashboardSettingsState {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  url: string;
  setUrl: (url: string) => void;
  tabName: string;
  setTabName: (tabName: string) => void;
  attemptedSave: boolean;
  saving: boolean;
  urlError: boolean;
  tabNameError: boolean;
  save: () => Promise<void>;
}

export const useCustomDashboardSettings = (
  initialValue: EditableCustomDashboardConfig,
  onSave: (value: EditableCustomDashboardConfig) => Promise<void | undefined>,
): CustomDashboardSettingsState => {
  const [enabled, setEnabled] = useState(initialValue.enabled);
  const [url, setUrl] = useState(initialValue.url);
  const [tabName, setTabName] = useState(initialValue.tabName);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const normalizedUrl = url.trim();
  const normalizedTabName = tabName.trim();
  const urlError = enabled && !isValidDashboardUrl(normalizedUrl);
  const tabNameError = enabled && !normalizedTabName;

  const save = async (): Promise<void> => {
    setAttemptedSave(true);
    if (urlError || tabNameError) return;

    setSaving(true);
    try {
      await onSave({
        id: initialValue.id,
        enabled,
        url: normalizedUrl,
        tabName: normalizedTabName,
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    enabled,
    setEnabled,
    url,
    setUrl,
    tabName,
    setTabName,
    attemptedSave,
    saving,
    urlError,
    tabNameError,
    save,
  };
};
